import { useNavigate } from "react-router-dom";
import { AppLayout } from "@/components/AppLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  ArrowLeft, 
  Zap, 
  ShieldCheck, 
  Compass, 
  Building2, 
  Search, 
  FileText,
  ArrowRight,
  Users,
  ClipboardCheck,
  GitCompare
} from "lucide-react";

const reportCards = [
  {
    title: "Job Insider Report",
    description: "Know what the hiring manager actually cares about before you apply. Deep intelligence on the role, company, and decision-maker—including pain points, salary data, and red flags most candidates never uncover.",
    icon: Zap,
    path: "/job-intel-reports",
    color: "from-orange-500 to-amber-500"
  },
  {
    title: "Job & Recruiter Legitimacy Report",
    description: "Real opportunity or waste of time? Find out before you apply. Verifies if jobs are active, companies are legitimate, and recruiters are who they claim—protecting you from ghost jobs and scams.",
    icon: ShieldCheck,
    path: "/job-legitimacy-reports",
    color: "from-emerald-500 to-teal-500"
  },
  {
    title: "Advanced Job Seeker Insights Report",
    description: "Company, department, and role intelligence in one package. Three-part research covering financials, team dynamics, interview questions, and strategic talking points—everything you need to walk in prepared.",
    icon: Building2,
    path: "/company-insights-reports",
    color: "from-purple-500 to-violet-500"
  },
  {
    title: "Hiring Manager ID Report",
    description: "Skip the ATS. Reach the decision-maker directly. Identifies who's actually hiring, with verified contact info, career background, and exactly how to approach them.",
    icon: Search,
    path: "/hiring-manager-reports",
    color: "from-pink-500 to-rose-500"
  },
  {
    title: "Solution Asset Report",
    description: "Replace your cover letter with proof you're the solution. A 1-page executive document that maps your experience to their pain points—plus a ready-to-use tool that delivers value before you're even hired.",
    icon: FileText,
    path: "/solution-asset-reports",
    color: "from-cyan-500 to-sky-500"
  },
  {
    title: "Inner Circle Report",
    description: "Your direct line to 100 companies and the people who hire. 500-1,000 decision-makers matched to your background—with LinkedIn profiles, titles, and why each contact matters for your search.",
    icon: Users,
    path: "/inner-circle",
    color: "from-indigo-500 to-purple-500"
  },
  {
    title: "Job Qualifications Match Report",
    description: "See exactly what the job is really asking for. Breaks down any job posting into clear requirements, responsibilities, and skills—so you know what matters before you apply.",
    icon: ClipboardCheck,
    path: "/?view=job-library",
    color: "from-amber-500 to-yellow-500"
  },
  {
    title: "Resume + Job Match Report",
    description: "Find out if you're a fit before they do. Side-by-side comparison of your resume against any job posting—showing where you match, where you fall short, and what to fix.",
    icon: GitCompare,
    path: "/?view=resume-match-tests",
    color: "from-lime-500 to-green-500"
  },
  {
    title: "Salary & Benefits Intelligence Report",
    description: "Know what the job actually pays — even when they say 'competitive salary.' Uncovers exact salary ranges, insurance providers by name, co-pays, PTO days, 401k match details, and total compensation value across 6+ data sources.",
    icon: Zap,
    path: "/salary-intel-reports",
    color: "from-green-500 to-emerald-500"
  },
  {
    title: "Advanced Job Search & Tracking Hub",
    description: "Complete, centralized control of your entire job search from a single dashboard. Monitors 300+ job boards, 150+ niche sites, and top company career pages — with instant alerts, auto-generated tailored documents, one-click apply, and full pipeline tracking.",
    icon: Zap,
    path: "/job-search-tracking-hub",
    color: "from-sky-500 to-indigo-500"
  }
];

const JobIntel360ReportsHub = () => {
  const navigate = useNavigate();

  return (
    <AppLayout currentPage="jobintel-reports-hub">
      <div className="container mx-auto py-8 px-4 max-w-6xl">
        <div className="flex flex-col items-center mb-8">
          <img src="/images/jobintel-360-header-logo.png" alt="JobIntel 360" className="h-14 mb-2" />
          <p className="text-muted-foreground mt-1">Access all your saved intelligence reports</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {reportCards.map((report) => {
            const IconComponent = report.icon;
            // Map gradient colors to solid background colors
            const cardStyleMap: Record<string, string> = {
              'from-orange-500 to-amber-500': 'border-orange-400 bg-orange-50 hover:border-orange-500',
              'from-emerald-500 to-teal-500': 'border-emerald-400 bg-emerald-50 hover:border-emerald-500',
              'from-blue-500 to-indigo-500': 'border-blue-400 bg-blue-50 hover:border-blue-500',
              'from-purple-500 to-violet-500': 'border-purple-400 bg-purple-50 hover:border-purple-500',
              'from-pink-500 to-rose-500': 'border-pink-400 bg-pink-50 hover:border-pink-500',
              'from-cyan-500 to-sky-500': 'border-cyan-400 bg-cyan-50 hover:border-cyan-500',
              'from-indigo-500 to-purple-500': 'border-indigo-400 bg-indigo-50 hover:border-indigo-500',
              'from-amber-500 to-yellow-500': 'border-amber-400 bg-amber-50 hover:border-amber-500',
              'from-lime-500 to-green-500': 'border-green-400 bg-green-50 hover:border-green-500',
              'from-green-500 to-emerald-500': 'border-emerald-400 bg-emerald-50 hover:border-emerald-500',
              'from-sky-500 to-indigo-500': 'border-sky-400 bg-sky-50 hover:border-sky-500',
            };
            const cardStyles = cardStyleMap[report.color] || 'border-primary/30 bg-primary-light';
            
            return (
              <Card 
                key={report.path}
                className={`cursor-pointer hover:shadow-xl transition-all duration-300 border-2 group hover:scale-[1.02] ${cardStyles}`}
                onClick={() => navigate(report.path)}
              >
                <CardHeader>
                  <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${report.color} flex items-center justify-center mb-4 shadow-lg group-hover:shadow-xl group-hover:scale-105 transition-all duration-300`}>
                    <IconComponent className="w-7 h-7 text-white" />
                  </div>
                  <CardTitle className="text-lg font-bold group-hover:text-primary transition-colors">
                    {report.title}
                  </CardTitle>
                  <CardDescription className="text-sm leading-relaxed">
                    {report.description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center text-primary text-sm font-semibold group-hover:gap-2 transition-all">
                    View Reports
                    <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-2 transition-transform duration-300" />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </AppLayout>
  );
};

export default JobIntel360ReportsHub;
